import React from "react";
import {
  WifiOutlined,
  DesktopOutlined,
  ToolOutlined,
  ShoppingOutlined,
  StarFilled,
  WarningFilled,
} from "@ant-design/icons";

type GearItem = {
  url: string;
  name: string;
  image: string;
  description?: string;
};

type GearSection = {
  title: string;
  blurb?: string;
  icon: React.ReactNode;
  items: GearItem[];
};

const gearSections: GearSection[] = [
  {
    title: "Router",
    icon: <WifiOutlined />,
    blurb:
      "We recommend a decent router with Wifi 6 support. For locations with a lot of network traffic, Wifi 6e (6GHz band) is recommended.",
    items: [
      {
        url: "https://www.amazon.com/WiFi-6-Router-Gigabit-Wireless/dp/B08H8ZLKKK/ref=sr_1_3?",
        name: "TP-Link AX1800 WiFi 6 Router",
        image: "/images/gear/TPLinkAX1800.jpg",
        description:
          "Archer AX21 — affordable WiFi 6 with strong throughput and stable performance under load.",
      },
    ],
  },
  {
    title: "PC / Laptop",
    icon: <DesktopOutlined />,
    blurb:
      "Make sure your machine meets at least the minimum specifications below.",
    items: [
      {
        url: "https://www.amazon.com/Octa-Core-GeForce-Display-Backlit-AN17-41-R8N5/dp/B0BZST2MBK/",
        name: "Acer Nitro 17 Gaming Laptop",
        image: "/images/gear/GamingLaptop.jpg",
        description:
          "AMD Ryzen 7 7735HS · NVIDIA RTX 4050 · 16 GB RAM · 1 TB SSD — a great out-of-the-box choice that exceeds our minimum requirements.",
      },
    ],
  },
  {
    title: "Accessories",
    icon: <ToolOutlined />,
    blurb: "Optional add-ons",
    items: [
      {
        url: "https://vrcover.com/item/fitness-facial-interface-and-foam-set-for-oculus-quest-2/",
        name: "VR Cover Fitness Facial Interface",
        image: "/images/gear/vrCover.jpg",
        description:
          "Sweat-resistant silicone facial interface for Quest 2 — easy to clean between players.",
      },
      {
        url: "https://www.bhaptics.com/tactsuit/tactsuit-x40",
        name: "bHaptics TactSuit X40",
        image: "/images/gear/bhaptics.jpg",
        description:
          "Wireless haptic vest with 40 feedback points — adds physical sensation to gameplay for next-level immersion.",
      },
    ],
  },
];

type Spec = {
  label: string;
  value: string;
  critical?: boolean;
};

const minimumSpecs: Spec[] = [
  {
    label: "GPU",
    value: "NVIDIA GeForce RTX 3060 / RTX 4050 or better",
    critical: true,
  },
  {
    label: "CPU",
    value: "Intel Core i5 (10th gen+) or AMD Ryzen 5 5600",
  },
  { label: "RAM", value: "16 GB" },
  { label: "Storage", value: "512 GB SSD" },
  { label: "OS", value: "Windows 10 or 11 (64-bit)" },
];

export const RecommendedGear = () => {
  return (
    <div className="recommended-gear-page">
      <div className="recommended-gear-header">
        <h1>Recommended Gear</h1>
        <p>Hardware we recommend for the best Hero Zone experience.</p>
      </div>

      {gearSections.map((section) => (
        <section key={section.title} className="gear-section">
          <div className="gear-section-header">
            <span className="gear-section-icon">{section.icon}</span>
            <div className="gear-section-heading">
              <h2>{section.title}</h2>
              {section.blurb && <p>{section.blurb}</p>}
            </div>
          </div>

          {section.title === "PC / Laptop" && (
            <div className="min-specs-panel">
              <div className="min-specs-title">Minimum Specifications</div>
              <ul className="min-specs-list">
                {minimumSpecs.map((spec) => (
                  <li
                    key={spec.label}
                    className={spec.critical ? "min-specs-row--critical" : ""}
                  >
                    {spec.critical ? (
                      <StarFilled className="min-specs-check min-specs-check--critical" />
                    ) : (
                      <span className="min-specs-bullet" aria-hidden="true">
                        •
                      </span>
                    )}
                    <span className="min-specs-label">{spec.label}</span>
                    <span className="min-specs-value">
                      {spec.value}
                      {spec.critical && (
                        <span className="min-specs-badge">Most important</span>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
              <div className="gear-warning-note">
                <WarningFilled className="gear-warning-icon" />
                <span>
                  Laptops <strong>without a dedicated GPU</strong> (integrated
                  graphics only) are <strong>not supported</strong>. A discrete
                  NVIDIA or AMD graphics card is required.
                </span>
              </div>
            </div>
          )}

          <div className="gear-grid">
            {section.items.map((item) => (
              <a
                key={item.name}
                href={item.url}
                target="_blank"
                rel="noreferrer"
                className="gear-card"
              >
                <div className="gear-card-image">
                  <img src={item.image} alt={item.name} />
                </div>
                <div className="gear-card-info">
                  <div className="gear-card-name">{item.name}</div>
                  {item.description && (
                    <div className="gear-card-description">
                      {item.description}
                    </div>
                  )}
                  <span className="gear-card-link">
                    <ShoppingOutlined />
                    View on Amazon
                  </span>
                </div>
              </a>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
};
